import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export default function PromoAdminList() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [tripTypeFilter, setTripTypeFilter] = useState("");
  const [promoTypeFilter, setPromoTypeFilter] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkTargets, setBulkTargets] = useState([]);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [fetchingAllInactive, setFetchingAllInactive] = useState(false);

  // Replaces window.confirm for single-row Deactivate / Delete actions
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'deactivate' | 'delete', promo }
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Pass every filter value explicitly so there are no stale-closure surprises
  const fetchPromos = async (opts = {}) => {
    const nextPage          = opts.page          ?? page;
    const nextLimit         = opts.limit         ?? limit;
    const nextSearch        = opts.search        ?? search;
    const nextStatus        = opts.status        ?? status;
    const nextTripType      = opts.tripTypeFilter ?? tripTypeFilter;
    const nextPromoType     = opts.promoTypeFilter ?? promoTypeFilter;

    setLoading(true);
    setErr("");

    try {
      const res = await api.get("/admin/promo-codes", {
        params: {
          search:               nextSearch || undefined,
          status:               nextStatus !== "" ? nextStatus : undefined,
          promo_code_trip_type: nextTripType !== "" ? nextTripType : undefined,
          promo_code_type:      nextPromoType !== "" ? nextPromoType : undefined,
          page:                 nextPage,
          limit:                nextLimit,
          sortBy:               "created_at",
          order:                "DESC",
        },
      });

      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, limit: nextLimit, totalPages: 1 });
      setSelectedIds([]);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load promo codes";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch whenever a dropdown filter changes (no Apply click needed)
  useEffect(() => {
    setPage(1);
    fetchPromos({ page: 1, search, status, tripTypeFilter, promoTypeFilter });
  }, [tripTypeFilter, promoTypeFilter, status]);

  const onApply = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPromos({ page: 1, search, status, tripTypeFilter, promoTypeFilter });
  };

  // ✅ Redirect Create
  const openCreate = () => {
    navigate("/admin/promo-codes/create");
  };

  // ✅ Redirect Edit
  const openEdit = (promo) => {
    navigate(`/admin/promo-codes/edit/${promo.promo_id}`);
  };

  const askDeactivate = (promo) => setConfirmAction({ type: "deactivate", promo });
  const askDelete = (promo) => setConfirmAction({ type: "delete", promo });

  const runConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, promo } = confirmAction;
    setConfirmBusy(true);
    try {
      if (type === "deactivate") {
        await api.delete(`/admin/promo-codes/${promo.promo_id}`);
        toast.success("Promo deactivated");
      } else {
        await api.delete(`/admin/promo-codes/${promo.promo_id}/hard`);
        toast.success("Promo code deleted");
        setSelectedIds((ids) => ids.filter((id) => id !== promo.promo_id));
      }
      setConfirmAction(null);
      await fetchPromos({ search, status, tripTypeFilter, promoTypeFilter });
    } catch (e) {
      const fallback = type === "deactivate" ? "Deactivate failed" : "Delete failed";
      toast.error(e?.response?.data?.message || e.message || fallback);
    } finally {
      setConfirmBusy(false);
    }
  };

  const inactiveRows = rows.filter((p) => Number(p.status) !== 1);
  const inactiveSelectedIds = selectedIds.filter((id) =>
    inactiveRows.some((p) => p.promo_id === id)
  );

  const toggleSelect = (promo_id) => {
    setSelectedIds((ids) =>
      ids.includes(promo_id) ? ids.filter((id) => id !== promo_id) : [...ids, promo_id]
    );
  };

  const toggleSelectAllInactive = (e) => {
    if (e.target.checked) {
      setSelectedIds((ids) => [
        ...ids,
        ...inactiveRows.map((p) => p.promo_id).filter((id) => !ids.includes(id)),
      ]);
    } else {
      setSelectedIds((ids) => ids.filter((id) => !inactiveRows.some((p) => p.promo_id === id)));
    }
  };

  const selectedPromos = rows.filter((p) => inactiveSelectedIds.includes(p.promo_id));

  const openSelectedDeleteModal = () => {
    setBulkTargets(selectedPromos);
    setShowBulkConfirm(true);
  };

  // Fetches every inactive promo code across all pages (not just the current page)
  const openAllInactiveDeleteModal = async () => {
    setFetchingAllInactive(true);
    try {
      const pageSize = 100;
      let all = [];
      let p = 1;
      let totalPages = 1;
      do {
        const res = await api.get("/admin/promo-codes", {
          params: { status: 0, page: p, limit: pageSize, sortBy: "created_at", order: "DESC" },
        });
        all = all.concat(res.data?.data || []);
        totalPages = res.data?.meta?.totalPages || 1;
        p += 1;
      } while (p <= totalPages);

      if (all.length === 0) {
        toast.error("No inactive promo codes to delete");
        return;
      }

      setBulkTargets(all);
      setShowBulkConfirm(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load inactive promo codes");
    } finally {
      setFetchingAllInactive(false);
    }
  };

  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const res = await api.post("/admin/promo-codes/bulk-delete", {
        promo_ids: bulkTargets.map((p) => p.promo_id),
      });
      const { deletedCount = 0, skipped = [] } = res.data?.data || {};
      toast.success(`${deletedCount} promo code(s) deleted`);
      if (skipped.length) {
        toast.error(`Skipped active codes: ${skipped.join(", ")}`);
      }
      setSelectedIds([]);
      setBulkTargets([]);
      setShowBulkConfirm(false);
      await fetchPromos({ search, status, tripTypeFilter, promoTypeFilter });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const goPrev = () => {
    if (meta.page <= 1) return;
    const p = meta.page - 1;
    setPage(p);
    fetchPromos({ page: p, search, status, tripTypeFilter, promoTypeFilter });
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    const p = meta.page + 1;
    setPage(p);
    fetchPromos({ page: p, search, status, tripTypeFilter, promoTypeFilter });
  };

  const onLimitChange = (e) => {
    const v = Number(e.target.value) || 10;
    setLimit(v);
    setPage(1);
    fetchPromos({ page: 1, limit: v, search, status, tripTypeFilter, promoTypeFilter });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Promo Codes</h1>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            className="rounded-xl border border-red-200 px-4 py-2 text-red-600 text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
            onClick={openAllInactiveDeleteModal}
            disabled={fetchingAllInactive}
            type="button"
          >
            {fetchingAllInactive ? "Loading..." : "Delete Inactive Promo Codes"}
          </button>

          <button
            className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
            onClick={openCreate}
          >
            + Create Promo
          </button>
        </div>
      </div>

      <form onSubmit={onApply} className="mt-6 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-3 border rounded-2xl p-4">
        <div className="w-full sm:w-auto sm:min-w-[240px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Search by code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[200px]">
          <label className="text-sm font-semibold">Status</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>

        <div className="w-full sm:w-auto sm:min-w-[160px]">
          <label className="text-sm font-semibold">Type</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={tripTypeFilter}
            onChange={(e) => setTripTypeFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="FLIGHT">✈ Flight</option>
            <option value="BUS">🚌 Bus</option>
          </select>
        </div>

        <div className="w-full sm:w-auto sm:min-w-[160px]">
          <label className="text-sm font-semibold">Promo Type</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={promoTypeFilter}
            onChange={(e) => setPromoTypeFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="NORMAL">NORMAL</option>
            <option value="NAVCASH">NAVCASH</option>
            <option value="UPI">UPI</option>
            <option value="TARGETED">TARGETED</option>
          </select>
        </div>

        <div className="w-full sm:w-auto sm:min-w-[140px]">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={limit}
            onChange={onLimitChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        <button
          type="submit"
          className="w-full sm:w-auto rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
        >
          Apply
        </button>
      </form>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <span>Promo List</span>
            {inactiveSelectedIds.length > 0 ? (
              <button
                type="button"
                onClick={openSelectedDeleteModal}
                className="rounded-lg bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
              >
                Delete Selected ({inactiveSelectedIds.length})
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              onClick={goPrev}
              disabled={meta.page <= 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Prev
            </button>

            <span className="text-gray-700">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: <b>{meta.total}</b>)
            </span>

            <button
              onClick={goNext}
              disabled={meta.page >= meta.totalPages || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No promo codes found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={inactiveRows.length > 0 && inactiveRows.every((p) => selectedIds.includes(p.promo_id))}
                    onChange={toggleSelectAllInactive}
                    disabled={inactiveRows.length === 0}
                    title="Select all inactive promo codes"
                  />
                </th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Promo Type</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Discount</th>
                <th className="px-4 py-3">Trip</th>
                <th className="px-4 py-3">Max/User</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>

            <tbody className="border-t">
              {rows.map((p) => {
                const promoCodeType = p.promo_code_type || "NORMAL";
                const PROMO_TYPE_STYLES = {
                  NORMAL: "bg-slate-100 text-slate-700",
                  NAVCASH: "bg-blue-100 text-blue-700",
                  UPI: "bg-emerald-100 text-emerald-700",
                  TARGETED: "bg-purple-100 text-purple-700",
                };

                const discountDisplay = (() => {
                  if (promoCodeType === "UPI") {
                    // Discount rate (2.5%) is hardcoded and never shown — only the rupee cap.
                    return p.max_booking_discount_amount
                      ? `Upto ₹${p.max_booking_discount_amount}`
                      : "—";
                  }
                  if (promoCodeType === "NAVCASH") {
                    return p.cashback_type === "PERCENT"
                      ? `${p.cashback_value}% cashback`
                      : `₹${p.cashback_value} cashback`;
                  }
                  return p.discount_type === "PERCENT"
                    ? `${p.discount_value}%`
                    : `₹${p.discount_value}`;
                })();

                const isInactive = Number(p.status) !== 1;

                return (
                <tr key={p.promo_id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(p.promo_id)}
                      onChange={() => toggleSelect(p.promo_id)}
                      disabled={!isInactive}
                      title={isInactive ? "Select for bulk delete" : "Only inactive promo codes can be selected"}
                    />
                  </td>
                  <td className="px-4 py-3 font-semibold">{p.code}</td>

                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                        PROMO_TYPE_STYLES[promoCodeType] || PROMO_TYPE_STYLES.NORMAL
                      }`}
                    >
                      {promoCodeType}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                      p.promo_code_trip_type === "BUS"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-sky-100 text-sky-700"
                    }`}>
                      {p.promo_code_trip_type === "BUS" ? "🚌 Bus" : "✈ Flight"}
                    </span>
                  </td>

                  <td className="px-4 py-3">{discountDisplay}</td>

                  <td className="px-4 py-3">{p.trip_type}</td>
                  <td className="px-4 py-3">{p.max_usage_per_user}</td>

                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                        Number(p.status) === 1
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {Number(p.status) === 1 ? "Active" : "Inactive"}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => openEdit(p)}
                      >
                        Edit
                      </button>

                      {Number(p.status) === 1 ? (
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                          onClick={() => askDeactivate(p)}
                        >
                          Deactivate
                        </button>
                      ) : null}

                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        onClick={() => askDelete(p)}
                        disabled={!isInactive}
                        title={isInactive ? "Delete permanently" : "Deactivate before deleting"}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {showBulkConfirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-bold">Delete {bulkTargets.length} promo code(s)?</h2>
            <p className="mt-1 text-sm text-gray-600">
              This will permanently remove the following inactive promo codes. This cannot be undone.
            </p>

            <div className="mt-4 max-h-64 overflow-y-auto rounded-xl border">
              <ul className="divide-y text-sm">
                {bulkTargets.map((p) => (
                  <li key={p.promo_id} className="px-4 py-2 flex items-center justify-between">
                    <span className="font-semibold">{p.code}</span>
                    <span className="text-gray-500">{p.promo_code_type || "NORMAL"}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                onClick={() => {
                  setShowBulkConfirm(false);
                  setBulkTargets([]);
                }}
                disabled={bulkDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
              >
                {bulkDeleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmAction ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-bold">
              {confirmAction.type === "deactivate" ? "Deactivate this promo?" : "Delete this promo permanently?"}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {confirmAction.type === "deactivate"
                ? `"${confirmAction.promo.code}" will be marked inactive.`
                : `"${confirmAction.promo.code}" will be permanently removed. This cannot be undone.`}
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                onClick={() => setConfirmAction(null)}
                disabled={confirmBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`rounded-xl px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 ${
                  confirmAction.type === "deactivate" ? "bg-black" : "bg-red-600"
                }`}
                onClick={runConfirmAction}
                disabled={confirmBusy}
              >
                {confirmBusy ? "Working..." : confirmAction.type === "deactivate" ? "Deactivate" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
